import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Navigation, Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { getPropertyType } from "@/lib/propertyTypes";
import {
  haversine,
  formatDistance,
  unitPrice,
  formatUnitPrice,
} from "@/lib/detailSchemas";
import { formatApiError } from "@/context/AuthContext";

const RADIUS_M = 3000;

export default function NearbySearchDialog({ open, onOpenChange, items = [], onSelect }) {
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [province, setProvince] = useState(null);
  const [district, setDistrict] = useState(null);
  const [neighborhood, setNeighborhood] = useState(null);
  const [ada, setAda] = useState("");
  const [parsel, setParsel] = useState("");
  const [loadingP, setLoadingP] = useState(false);
  const [loadingD, setLoadingD] = useState(false);
  const [loadingN, setLoadingN] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoadingP(true);
      try {
        const { data } = await api.get("/locations/provinces");
        setProvinces(data.items);
      } catch (e) {
        toast.error("İl listesi yüklenemedi");
      } finally {
        setLoadingP(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    setDistricts([]);
    setDistrict(null);
    setNeighborhoods([]);
    setNeighborhood(null);
    if (!province) return;
    (async () => {
      setLoadingD(true);
      try {
        const { data } = await api.get("/locations/districts", {
          params: { province_id: province.id },
        });
        setDistricts(data.items);
      } catch (e) {
        toast.error("İlçe listesi yüklenemedi");
      } finally {
        setLoadingD(false);
      }
    })();
  }, [province]);

  useEffect(() => {
    setNeighborhoods([]);
    setNeighborhood(null);
    if (!district) return;
    (async () => {
      setLoadingN(true);
      try {
        const { data } = await api.get("/locations/neighborhoods", {
          params: { district_id: district.id },
        });
        setNeighborhoods(data.items);
      } catch (e) {
        toast.error("Mahalle listesi yüklenemedi");
      } finally {
        setLoadingN(false);
      }
    })();
  }, [district]);

  const search = async () => {
    if (!province || !district || !neighborhood) {
      toast.error("Şehir, ilçe ve mahalle seçin");
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/locations/parsel-lookup", {
        params: {
          province: province.name,
          district: district.name,
          neighborhood: neighborhood.name,
        },
      });
      const center = { lat: data.latitude, lng: data.longitude };
      let found = items
        .map((it) => ({
          item: it,
          distance: haversine(center, { lat: it.latitude, lng: it.longitude }),
        }))
        .filter((r) => r.distance <= RADIUS_M)
        .sort((a, b) => a.distance - b.distance);

      // If ada/parsel typed, prioritize (exact match first, then contains)
      if (ada || parsel) {
        const adaQ = (ada || "").trim().toLocaleLowerCase("tr");
        const parselQ = (parsel || "").trim().toLocaleLowerCase("tr");
        found = found
          .map((r) => {
            const a = String(r.item.ada || "").toLocaleLowerCase("tr");
            const p = String(r.item.parsel || "").toLocaleLowerCase("tr");
            const adaExact = adaQ && a === adaQ;
            const parselExact = parselQ && p === parselQ;
            const adaContains = adaQ && a.includes(adaQ);
            const parselContains = parselQ && p.includes(parselQ);
            let score = 0;
            if (adaExact) score += 4;
            if (parselExact) score += 4;
            if (!adaExact && adaContains) score += 2;
            if (!parselExact && parselContains) score += 2;
            return { ...r, score, exactMatch: adaExact && parselExact };
          })
          .sort((a, b) => b.score - a.score || a.distance - b.distance);
      }
      setResults({ center, found });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Konum bulunamadı");
    } finally {
      setSearching(false);
    }
  };

  const reset = () => {
    setProvince(null);
    setDistrict(null);
    setNeighborhood(null);
    setResults(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="sm:max-w-lg p-0 gap-0"
        data-testid="nearby-dialog"
      >
        <DialogHeader className="px-5 py-4 border-b border-zinc-200">
          <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2">
            <Navigation className="h-4 w-4 text-emerald-600" />
            Yakınımda Ara
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Bir mahalle seçin, 3 km yarıçapındaki arşiv kayıtlarınızı listeleyin.
          </p>
        </DialogHeader>

        <div className="px-5 py-4 space-y-3 border-b border-zinc-100">
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-zinc-700">Şehir</Label>
              <Select
                value={province?.id?.toString() || ""}
                onValueChange={(v) => setProvince(provinces.find((p) => p.id.toString() === v))}
                disabled={loadingP}
              >
                <SelectTrigger className="rounded-sm h-9" data-testid="nearby-province-select">
                  <SelectValue placeholder={loadingP ? "..." : "Seç"} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {provinces.map((p) => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-zinc-700">İlçe</Label>
              <Select
                value={district?.id?.toString() || ""}
                onValueChange={(v) => setDistrict(districts.find((d) => d.id.toString() === v))}
                disabled={!province || loadingD}
              >
                <SelectTrigger className="rounded-sm h-9" data-testid="nearby-district-select">
                  <SelectValue placeholder={!province ? "—" : loadingD ? "..." : "Seç"} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.id.toString()}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-zinc-700">Mahalle</Label>
              <Select
                value={neighborhood?.id?.toString() || ""}
                onValueChange={(v) => setNeighborhood(neighborhoods.find((n) => n.id.toString() === v))}
                disabled={!district || loadingN}
              >
                <SelectTrigger className="rounded-sm h-9" data-testid="nearby-neighborhood-select">
                  <SelectValue placeholder={!district ? "—" : loadingN ? "..." : "Seç"} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {neighborhoods.map((n) => (
                    <SelectItem key={n.id} value={n.id.toString()}>
                      {n.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-zinc-700">Ada (opsiyonel)</Label>
              <Input
                value={ada}
                onChange={(e) => setAda(e.target.value)}
                placeholder="Örn: 1234"
                className="rounded-sm h-9"
                data-testid="nearby-ada-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-zinc-700">Parsel (opsiyonel)</Label>
              <Input
                value={parsel}
                onChange={(e) => setParsel(e.target.value)}
                placeholder="Örn: 56"
                className="rounded-sm h-9"
                data-testid="nearby-parsel-input"
              />
            </div>
          </div>
          <Button
            type="button"
            onClick={search}
            disabled={searching || !neighborhood}
            className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-sm"
            data-testid="nearby-search-btn"
          >
            {searching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Search className="h-4 w-4 mr-1.5" />
                Yakınımı Ara
              </>
            )}
          </Button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {results === null ? (
            <div className="px-5 py-8 text-center">
              <div className="mx-auto h-10 w-10 grid place-items-center rounded-sm bg-zinc-100 text-zinc-400">
                <Navigation className="h-4 w-4" />
              </div>
              <p className="mt-3 text-xs text-zinc-500">
                Mahalle seçin ve arayın
              </p>
            </div>
          ) : results.found.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-zinc-700">3 km yarıçapında arşiv kaydınız yok.</p>
              <p className="mt-1 text-xs text-zinc-400">
                Bu mahalleye ait bir ekspertiz kaydınız görünmüyor.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="nearby-results">
              {results.found.map(({ item, distance, exactMatch }) => {
                const type = getPropertyType(item.property_type);
                const up = unitPrice(item);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelect && onSelect(item);
                        onOpenChange(false);
                      }}
                      className={`w-full text-left px-5 py-3 hover:bg-emerald-50 transition-colors ${
                        exactMatch ? "bg-amber-50" : ""
                      }`}
                      data-testid={`nearby-result-${item.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className="inline-block h-2 w-2 rounded-full shrink-0"
                            style={{ background: type.color }}
                          />
                          <span
                            className="text-[10px] font-semibold uppercase tracking-wide shrink-0"
                            style={{ color: type.color }}
                          >
                            {type.label}
                          </span>
                          <span className="text-[10px] text-zinc-400 font-mono truncate">
                            · A{item.ada}/P{item.parsel}
                          </span>
                          {exactMatch && (
                            <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-500 text-white rounded-sm px-1 py-0.5">
                              Tam Eşleşme
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] font-mono font-semibold text-emerald-700 shrink-0">
                          {formatDistance(distance)}
                        </span>
                      </div>
                      <div className="text-sm text-zinc-900 font-medium mt-0.5 truncate">
                        {item.neighborhood_name} Mah., {item.district_name}
                      </div>
                      {up && (
                        <div className="mt-1 inline-block text-[11px] font-mono font-bold text-amber-800 bg-amber-100/60 border border-amber-200 rounded-sm px-1.5 py-0.5">
                          {formatUnitPrice(up)}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
